"use client";

import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import { Message } from "@/lib/core/crm/types";

interface UseComposeUrlParamsProps {
  isOpen: boolean;
}

/**
 * Hook for parsing and managing URL parameters for compose modal
 * Extracts contactsId from URL and search params
 */
export function useComposeUrlParams({ isOpen }: UseComposeUrlParamsProps) {
  const searchParams = useSearchParams();
  const [urlContactData, setUrlContactData] = useState<{
    email?: string;
    name?: string;
  } | null>(null);

  const extractContactIdFromUrl = useCallback((): string | null => {
    // Try search params first
    let contactsId = searchParams?.get("contactsId") ?? null;

    if (!contactsId && typeof window !== "undefined") {
      // Extract from pathname: /contacts/[id]
      const pathMatch = window.location.pathname.match(/\/contacts\/([a-f0-9-]+)/);
      if (pathMatch && pathMatch[1]) {
        contactsId = pathMatch[1];
      }
    }

    return contactsId;
  }, [searchParams]);

  const resetUrlContactData = useCallback(() => {
    setUrlContactData(null);
  }, []);

  const setContactData = useCallback(
    (data: { email?: string; name?: string } | null) => {
      setUrlContactData(data);
    },
    []
  );

  return useMemo(
    () => ({
      urlContactData,
      isOpen,
      extractContactIdFromUrl,
      resetUrlContactData,
      setContactData,
    }),
    [urlContactData, isOpen, extractContactIdFromUrl, resetUrlContactData, setContactData]
  );
}
